import { createContext, useContext } from "react";
import { useSessionStorage } from "../hook/useStorage";

const VerificationContext = createContext(null);

const VerificationProvider = ({ children }) => {
  const [insuranceProvider, setInsuranceProvider, removeInsuranceProvider] =
    useSessionStorage("insuranceProvider", null);
    const [isResending, setIsResending, removeIsResending] =
  useSessionStorage("isResending", false);

  const [employeeId, setEmployeeId, removeEmployeeId] = useSessionStorage(
    "employeeId",
    0
  );

  const [dealershipId, setDealershipId, removeDealershipId] = useSessionStorage(
    "dealershipId",
    0
  );

  const [customer, setCustomer, removeCustomer] = useSessionStorage(
    "customer",
    null
  );
  const [workflow, setWorkflow, removeWorkflow] = useSessionStorage(
    "workflow",
    null
  );
  const [percentage, setPercentage, removePercentage] = useSessionStorage(
    "percentage",
    null
  );

  const [pullInsurance, setPullInsurance, removePullInsurance] =
    useSessionStorage("pullInsurance", null);

  const [lastConsentInsuranceInput, setLastConsentInsuranceInput, removeLastConsentInsuranceInput] =
    useSessionStorage("lastConsentInsuranceInput", null);

  const [dealershipLogo, setDealershipLogo, removeDealershipLogo] =
    useSessionStorage("dealershipLogo", null);
  const [artifacts, setArtifacts, removeArtifacts] = useSessionStorage(
    "artifacts",
    null
  );
  const [artifactsResponse, setArtifactsResponse, removeArtifactsResponse] =
    useSessionStorage("artifactsResponse", null);

  const [artifactIndex, setArtifactIndex, removeArtifactIndex] =
    useSessionStorage("artifactIndex", 0);

  const [isArtifactOpen, setIsArtifactOpen, removeIsArtifactOpen] =
    useSessionStorage("isArtifactOpen", null);

  const [isRepull, setIsRepull, removeIsRepull] = useSessionStorage(
    "isRepull",
    null
  );

  const [fetchData, setFetchData, removeFetchData] = useSessionStorage(
    "fetchData",
    null
  );

  const [status, setStatus, removeStatus] = useSessionStorage("status", null);

  const [openingHours, setOpeningHours, removeOpeningHours] = useSessionStorage(
    "openingHours",
    null
  );

  const [aiData, setAIData, removeAIData] = useSessionStorage("aiData", null);

  const [services, setServices, removeServices] = useSessionStorage(
    "services",
    null
  );
  const [checkService, setCheckService, removeCheckService] = useSessionStorage(
    "checkService",
    null
  );
  const [tradeQuote, setTradeQuote, removeTradeQuote] = useSessionStorage(
    "tradeQuote",
    null
  );

  const [hasSeenWalkthroughVideo, setHasSeenWalkthroughVideo, removeHasSeenWalkthroughVideo] = useSessionStorage(
    "hasSeenWalkthroughVideo",
    false
  );

  // Insurance Verification Workflow walkthrough (video_url) — separate from KH WalkMe
  const [
    hasSeenInsuranceWalkthrough,
    setHasSeenInsuranceWalkthrough,
    removeHasSeenInsuranceWalkthrough,
  ] = useSessionStorage("hasSeenInsuranceWalkthrough", false);

  const [walkthroughVideoUrl, setWalkthroughVideoUrl, removeWalkthroughVideoUrl] = useSessionStorage(
    "walkthroughVideoUrl",
    null
  );

  const [isWalkthrough, setIsWalkthrough, removeIsWalkthrough] = useSessionStorage(
    "isWalkthrough",
    false
  );

  const [isFromCustomerTable, setIsFromCustomerTable, removeIsFromCustomerTable] = useSessionStorage(
    "isFromCustomerTable",
    false
  );

  const [isExpressLogin, setIsExpressLogin, removeIsExpressLogin] = useSessionStorage(
    "isExpressLogin",
    false
  );

  // Customer omni/Express insurance-only (?service=1, no service=2).
  // Not the same as manager Verify Only workflow (workflow_id === 2).
  const [
    isInsuranceOnlyService,
    setIsInsuranceOnlyService,
    removeIsInsuranceOnlyService,
  ] = useSessionStorage("isInsuranceOnlyService", false);

  // Continue Delivery path: quote first, then KW + artifacts
  const [
    isContinueDelivery,
    setIsContinueDelivery,
    removeIsContinueDelivery,
  ] = useSessionStorage("isContinueDelivery", false);

  // Channel (email / text / device) the customer chose in the Verify Insurance
  // dialog. Set at Consent from ?via=, logged once on the thank-you screen when
  // the Knowledge Hub starts, then cleared.
  const [khChannel, setKhChannel, removeKhChannel] = useSessionStorage(
    "khChannel",
    null
  );

  const [vehicleDetail, setVehicleDetail, removeVehicleDetail] = useSessionStorage(
    "vehicleDetail",
    {
      vin: "",
      year: "",
      make: "",
      model: "",
    }
  );

  const removeAll = () => {
    removeInsuranceProvider();
    removeEmployeeId();
    removeCustomer();
    removePullInsurance();
    removeLastConsentInsuranceInput();
    removeDealershipId();
    removePercentage();
    removeWorkflow();
    removeDealershipLogo();
    removeArtifacts();
    removeArtifactsResponse();
    removeArtifactIndex();
    removeIsArtifactOpen();
    removeIsRepull();
    removeFetchData();
    removeStatus();
    removeOpeningHours();
    removeAIData();
    removeServices();
    removeCheckService();
    removeTradeQuote();
    removeHasSeenWalkthroughVideo();
    removeHasSeenInsuranceWalkthrough();
    removeWalkthroughVideoUrl();
    removeIsWalkthrough();
    removeIsFromCustomerTable();
    removeVehicleDetail();
    removeIsExpressLogin();
    removeIsInsuranceOnlyService();
    removeIsContinueDelivery();
    removeKhChannel();
  };

  const insuranceObject = insuranceProvider
    ? JSON.parse(insuranceProvider).provider
    : null;

  return (
    <VerificationContext.Provider
      value={{
        setInsuranceProvider,
        removeAll,
        insuranceObject,
        employeeId,
        setEmployeeId,
        customer,
        setCustomer,
        pullInsurance,
        setPullInsurance,
        lastConsentInsuranceInput,
        setLastConsentInsuranceInput,
        dealershipId,
        setDealershipId,
        workflow,
        setWorkflow,
        percentage,
        setPercentage,
        dealershipLogo,
        setDealershipLogo,
        artifacts,
        setArtifacts,
        artifactsResponse,
        setArtifactsResponse,
        artifactIndex,
        setArtifactIndex,
        isArtifactOpen,
        setIsArtifactOpen,
        isRepull,
        setIsRepull,
        fetchData,
        setFetchData,
        status,
        setStatus,
        openingHours,
        setOpeningHours,
        aiData,
        setAIData,
        services,
        setServices,
        checkService,
        setCheckService,
        tradeQuote,
        setTradeQuote,
        isResending,
        setIsResending,
        removeIsResending,
        hasSeenWalkthroughVideo,
        setHasSeenWalkthroughVideo,
        hasSeenInsuranceWalkthrough,
        setHasSeenInsuranceWalkthrough,
        walkthroughVideoUrl,
        setWalkthroughVideoUrl,
        isWalkthrough,
        setIsWalkthrough,
        isFromCustomerTable,
        setIsFromCustomerTable,
        vehicleDetail,
        setVehicleDetail,
        isExpressLogin,
        setIsExpressLogin,
        isInsuranceOnlyService,
        setIsInsuranceOnlyService,
        isContinueDelivery,
        setIsContinueDelivery,
        khChannel,
        setKhChannel,
      }}
    >
      {children}
    </VerificationContext.Provider>
  );
};

const useVerification = () => {
  return useContext(VerificationContext);
};

export { VerificationProvider, useVerification };